// pages/monitor/panels/ems/EmsDevice.js
// 《云平台（能源管家）需求文档》2.7.3 设备监控（p18–22）
// 差异 #8：PCS 开关机/复归、空调开关机位于设备监控内且需二次口令
// 差异 #11：本类型独有「视频监控」与「消防设备（联动环）」；无光伏逆变器、液冷、DCDC、UPS、充电桩
import React, { useState } from 'react';
import ScrollTabs from '../../../../components/ScrollTabs';
import { useLang, t } from '../../consoles/ems/langContext';
import { Segmented, Select, Button, Space, Tag, Table, Progress, Modal, message,
} from 'antd';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Brush, ReferenceLine,
} from 'recharts';
import { getDevices, getStation, getStationVideo, getOverview, getPowerCurve } from '../../../../config/stationData';
import { LAST_REPORT, PCS_STATES, BMS_STATES, FIRE_LINK_RING } from './data';
import './ems.css';

const tick = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const grid = 'rgba(255,255,255,0.08)';
const tooltipStyle = {
  background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff',
};

const pcsStateOf = (p, lang) => (p.alarm ? t(lang, '故障', 'Fault') : p.running ? t(lang, '运行', 'Running') : t(lang, '停机', 'Stopped'));

// 数据状态词 / 告警词英文（langContext 内置词典之外的补充；词典已覆盖的词经 t 两参兜底）
const EXTRA_EN = {
  '禁充': 'Charge Blocked', '簇维护': 'Cluster Maintenance',
  '通信中断': 'Comm Interrupted', '充电中': 'Charging',
  '直流侧绝缘偏低': 'DC Insulation Low', '单体压差偏大': 'Cell Voltage Deviation High',
  '回风温度偏高': 'Return Air Temp High',
};

// 设备操作区：口令未通过（unlocked===false）时渲染外层传入的 gate 节点
function ControlBar({ title, sub, unlocked, gate, lockedText = '需二次口令', children }) {
  return (
    <div className="panel">
      <div className="panel-title">
        <span>{title}</span>
        <small>{unlocked ? sub : lockedText}</small>
      </div>
      {unlocked ? children : gate}
    </div>
  );
}

function Kpi({ label, value, unit }) {
  return (
    <div className="dev-kv-row">
      <span className="dev-kv-k">{label}</span>
      <span className="dev-kv-v">{value}<span className="unit"> {unit}</span></span>
    </div>
  );
}

export default function EmsDevice({ station, unlocked, gate }) {
  const { lang } = useLang();
  const st = station || getStation('xz');
  const dev = getDevices(st.id);
  const video = getStationVideo(st.id);

  const [rackIdx, setRackIdx] = useState(0);
  const [clusterIdx, setClusterIdx] = useState(0);
  const [cellView, setCellView] = useState('table');
  const [hvacTemps, setHvacTemps] = useState(() => dev.hvac.map((h) => h.setTemp));
  const [fireModal, setFireModal] = useState(false);
  const [ringBound, setRingBound] = useState(false);

  const rack = dev.racks[rackIdx];
  // 每个电池堆拆分为 2 个电池簇（演示数据，待接口接入）；簇状态取自文档七态
  const clusters = [
    { name: '1 号簇', state: rack.workState, soc: rack.soc, voltage: rack.voltage, temp: rack.temp },
    { name: '2 号簇', state: rack.workState === '就绪' ? '禁放' : '就绪', soc: Math.max(5, rack.soc - 2), voltage: rack.voltage - 3, temp: rack.temp + 1 },
  ];
  const cells = dev.cells;

  // ---------- PCS ----------
  const renderPcs = () => (
    <>
      <div className="dev-grid">
        {dev.pcs.map((p) => (
          <div className="dev-card" key={p.id}>
            <div className="dev-card-head">
              <span className={`dot ${p.running ? 'online' : 'offline'}`} />
              <span className="dev-name">{p.name}</span>
              <span className="dev-status">{p.running ? t(lang, '运行', 'Running') : t(lang, '设备中断', 'Device Interrupted')}</span>
            </div>
            <div className="dev-tags">
              <Tag color="blue">{pcsStateOf(p, lang)}</Tag>
              <Tag>{p.grid}</Tag>
              <Tag>{p.mode}</Tag>
              {p.alarm ? <Tag color="orange">{t(lang, p.alarm, EXTRA_EN[p.alarm])}</Tag> : null}
              {!p.running ? <Tag color="red">{t(lang, '最后上报', 'Last report')} {LAST_REPORT}</Tag> : null}
            </div>
            <div className="dev-kv">
              <Kpi label={t(lang, '有功功率', 'Active Power')} value={p.p} unit="kW" />
              <Kpi label={t(lang, '无功功率', 'Reactive Power')} value={p.q} unit="kVar" />
              <Kpi label={t(lang, '功率因数', 'Power Factor')} value={p.pf} unit="—" />
              <Kpi label={t(lang, '频率', 'Frequency')} value={p.freq} unit="Hz" />
              <Kpi label={t(lang, '模块温度', 'Module Temp')} value={p.moduleTemp} unit="℃" />
              <Kpi label={t(lang, '环境温度', 'Ambient Temp')} value={p.envTemp} unit="℃" />
              <Kpi label={t(lang, '三相电压', '3-Phase Voltage')} value={p.uabc.join(' / ')} unit="V" />
              <Kpi label={t(lang, '三相电流', '3-Phase Current')} value={p.iabc.join(' / ')} unit="A" />
              <Kpi label={t(lang, '今日充电', 'Charge Today')} value={p.chargeToday} unit="kWh" />
              <Kpi label={t(lang, '今日放电', 'Discharge Today')} value={p.dischargeToday} unit="kWh" />
            </div>
          </div>
        ))}
      </div>

      <ControlBar title={t(lang, 'PCS 设备操作', 'PCS Device Operation')} sub={t(lang, '开关机与故障复归需二次口令', 'Power on/off and fault reset require password verification')} lockedText={t(lang, '需二次口令', 'Password Required')} unlocked={unlocked} gate={gate}>
        <div className="dev-grid-2">
          {dev.pcs.map((p) => (
            <div className="dev-card" key={p.id}>
              <div className="dev-card-head"><span className="dev-name">{p.name}</span><span className="dev-status">{pcsStateOf(p, lang)}</span></div>
              <Space wrap>
                <Button size="small" type={p.running ? 'default' : 'primary'} onClick={() => message.success(t(lang, `${p.name} 下发「${p.running ? '关机' : '开机'}」指令成功`, `"${p.running ? 'Power Off' : 'Power On'}" command sent to ${p.name} successfully`))}>
                  {p.running ? t(lang, '关机', 'Power Off') : t(lang, '开机', 'Power On')}
                </Button>
                <Button size="small" onClick={() => message.success(t(lang, `${p.name} 故障复归指令成功`, `Fault reset command sent to ${p.name} successfully`))}>{t(lang, '故障复归', 'Fault Reset')}</Button>
              </Space>
            </div>
          ))}
        </div>
        <div className="ems-note">{t(lang, '开机/关机按钮按当前运行状态显示对应文案；开机、关机、故障复归下发前均需二次口令校验。', 'Power on/off buttons show text per current running state; power on, power off, and fault reset all require password verification before issuing.')}</div>
      </ControlBar>

      <div className="panel">
        <div className="panel-title"><span>{t(lang, 'PCS 直流支路', 'PCS DC Branch')}</span><small>{t(lang, '一条直流支路一行 · 充放电状态取自 PCS 遥信', 'One DC branch per row · charge/discharge state from PCS telemetry')}</small></div>
        <Table
          size="small"
          pagination={false}
          rowKey={(r) => `${r.pcs}-${r.branch}`}
          columns={[
            { title: 'PCS', dataIndex: 'pcs' },
            { title: t(lang, '直流支路', 'DC Branch'), dataIndex: 'branch' },
            { title: t(lang, '直流功率 (kW)', 'DC Power (kW)'), dataIndex: 'p', align: 'right' },
            { title: t(lang, '电压 (V)', 'Voltage (V)'), dataIndex: 'u', align: 'right' },
            { title: t(lang, '电流 (A)', 'Current (A)'), dataIndex: 'i', align: 'right' },
            {
              title: t(lang, '充放电状态', 'Charge/Discharge State'), dataIndex: 'charging',
              render: (v) => (v ? <Tag color="green">{t(lang, '充电 (true)', 'Charging (true)')}</Tag> : <Tag color="default">{t(lang, '放电 (false)', 'Discharging (false)')}</Tag>),
            },
          ]}
          dataSource={dev.pcs.flatMap((p) => p.branches.map((b) => ({ pcs: p.name, branch: b.name, p: b.p, u: b.u, i: b.i, charging: b.p > 0 })))}
        />
        <div className="ems-note">{t(lang, `PCS 四态（${PCS_STATES.join(' / ')}）取自 PCS 遥信；充放电状态 true 为绿色、false 为灰色。`, `PCS four states (${PCS_STATES.map((s) => t(lang, s, EXTRA_EN[s])).join(' / ')}) from PCS telemetry; charge/discharge state true is green, false is gray.`)}</div>
      </div>
    </>
  );

  // ---------- BMS 总览 ----------
  const renderBms = () => (
    <>
      <div className="dev-grid">
        {dev.racks.map((r) => (
          <div className="dev-card" key={r.id}>
            <div className="dev-card-head">
              <span className="dot online" />
              <span className="dev-name">{r.name}</span>
              <span className="dev-status">{t(lang, r.workState, EXTRA_EN[r.workState])}</span>
            </div>
            <div className="dev-tags">
              <Tag color="blue">{t(lang, r.workState, EXTRA_EN[r.workState])}</Tag>
              <Tag>{t(lang, '正极', 'Positive')} {t(lang, r.contactorPos, EXTRA_EN[r.contactorPos])}</Tag>
              <Tag>{t(lang, '负极', 'Negative')} {t(lang, r.contactorNeg, EXTRA_EN[r.contactorNeg])}</Tag>
              {r.alarm ? <Tag color="orange">{t(lang, r.alarm, EXTRA_EN[r.alarm])}</Tag> : null}
            </div>
            <div className="dev-kv">
              <Kpi label={t(lang, '总电压', 'Total Voltage')} value={r.voltage} unit="V" />
              <Kpi label={t(lang, '电流', 'Current')} value={r.current} unit="A" />
              <Kpi label={t(lang, '温度', 'Temperature')} value={r.temp} unit="℃" />
              <Kpi label="SOH" value={r.soh} unit="%" />
              <Kpi label={t(lang, '可用电量', 'Available Energy')} value={r.available} unit="kWh" />
              <Kpi label={t(lang, '单体数', 'Cell Count')} value={r.cells} unit="只" />
            </div>
            <div style={{ marginTop: 8 }}>
              <div className="dev-kv-k">SOC {r.soc}%</div>
              <Progress percent={r.soc} showInfo={false} strokeColor="#52e8b0" trailColor="rgba(255,255,255,0.1)" />
            </div>
          </div>
        ))}
      </div>
      <div className="ems-note">{t(lang, `电堆状态：${BMS_STATES.join(' / ')}（取自 BMS 遥信）。`, `Pack states: ${BMS_STATES.map((s) => t(lang, s, EXTRA_EN[s])).join(' / ')} (from BMS telemetry).`)}</div>
      <ControlBar title={t(lang, 'BMS 设备操作', 'BMS Device Operation')} sub={t(lang, '故障复归需二次口令', 'Fault reset requires password verification')} lockedText={t(lang, '需二次口令', 'Password Required')} unlocked={unlocked} gate={gate}>
        <Space wrap>
          {dev.racks.map((r) => (
            <Button key={r.id} size="small" onClick={() => message.success(t(lang, `${r.name} BMS 故障复归指令成功`, `BMS fault reset command sent to ${r.name} successfully`))}>{r.name} {t(lang, '故障复归', 'Fault Reset')}</Button>
          ))}
        </Space>
      </ControlBar>
    </>
  );

  // ---------- BMS 电池簇 ----------
  const renderCluster = () => (
    <>
      <div className="ems-inline">
        <span className="ems-inline-label">{t(lang, '设备切换', 'Device Switch')}</span>
        <Select
          value={rackIdx}
          style={{ minWidth: 180 }}
          onChange={(v) => { setRackIdx(v); setClusterIdx(0); }}
          options={dev.racks.map((r, i) => ({ value: i, label: r.name }))}
        />
      </div>
      <div className="dev-grid dev-grid-2">
        {clusters.map((c, i) => (
          <div className={`dev-card${i === clusterIdx ? ' fault' : ''}`} key={c.name} onClick={() => setClusterIdx(i)} style={{ cursor: 'pointer' }}>
            <div className="dev-card-head">
              <span className={`dot ${c.state === '故障' ? 'fault' : 'online'}`} />
              <span className="dev-name">{rack.name} · {c.name}</span>
              <span className="dev-status">{t(lang, c.state, EXTRA_EN[c.state])}</span>
            </div>
            <div className="dev-tags"><Tag color="blue">{t(lang, c.state, EXTRA_EN[c.state])}</Tag><Tag>{t(lang, '接触器 闭合', 'Contactor Closed')}</Tag></div>
            <div className="dev-kv">
              <Kpi label={t(lang, '电压', 'Voltage')} value={c.voltage} unit="V" />
              <Kpi label={t(lang, '温度', 'Temperature')} value={c.temp} unit="℃" />
            </div>
            <div style={{ marginTop: 8 }}>
              <div className="dev-kv-k">SOC {c.soc}%</div>
              <Progress percent={c.soc} showInfo={false} strokeColor="#52e8b0" trailColor="rgba(255,255,255,0.1)" />
            </div>
          </div>
        ))}
      </div>
      <div className="panel">
        <div className="panel-title"><span>{t(lang, '电池簇 SOC', 'Cluster SOC')}</span><small>{rack.name}</small></div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={clusters} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid stroke={grid} vertical={false} />
            <XAxis dataKey="name" tick={tick} stroke={grid} />
            <YAxis domain={[0, 100]} tick={tick} stroke={grid} unit="%" width={44} />
            <RTooltip contentStyle={tooltipStyle} />
            <Bar dataKey="soc" name="SOC" fill="#52e8b0" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </>
  );

  // ---------- 单体电池 ----------
  const renderCells = () => (
    <>
      <div className="ems-inline">
        <span className="ems-inline-label">{t(lang, '设备切换', 'Device Switch')}</span>
        <Select
          value={`${rackIdx}-${clusterIdx}`}
          style={{ minWidth: 240 }}
          onChange={(v) => { const [r, c] = v.split('-').map(Number); setRackIdx(r); setClusterIdx(c); }}
          options={dev.racks.flatMap((r, ri) => [
            { value: `${ri}-0`, label: `${r.name} · 1 号簇` },
            { value: `${ri}-1`, label: `${r.name} · 2 号簇` },
          ])}
        />
        <span className="ems-inline-label">{t(lang, '视图切换', 'View Mode')}</span>
        <Segmented
          value={cellView}
          onChange={setCellView}
          options={[{ value: 'table', label: t(lang, '表格', 'Table') }, { value: 'bar', label: t(lang, '柱状图', 'Bar Chart') }]}
        />
      </div>
      {cellView === 'table' ? (
        <div className="panel">
          <div className="panel-title"><span>{t(lang, '单体数据', 'Cell Data')}</span><small>{rack.name} · 2 号簇 · {t(lang, `共 ${cells.length} 只`, `${cells.length} cells in total`)}</small></div>
          <Table
            size="small"
            rowKey="no"
            pagination={{ pageSize: 12, showSizeChanger: false }}
            columns={[
              { title: t(lang, '单体编号', 'Cell No.'), dataIndex: 'no' },
              { title: t(lang, '电压 (V)', 'Voltage (V)'), dataIndex: 'voltage', render: (v) => v.toFixed(3) },
              { title: t(lang, '温度 (℃)', 'Temperature (℃)'), dataIndex: 'temp', render: (v) => v.toFixed(1) },
              { title: 'SOC (%)', dataIndex: 'soc' },
              { title: 'SOH (%)', dataIndex: 'soh' },
            ]}
            dataSource={cells}
          />
        </div>
      ) : (
        <div className="panel">
          <div className="panel-title"><span>{t(lang, '单体电压柱状图', 'Cell Voltage Bar Chart')}</span><small>{t(lang, '支持 X/Y 轴缩放（框选拖拽缩放）', 'Supports X/Y axis zoom (drag-box zoom)')}</small></div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={cells} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid stroke={grid} vertical={false} />
              <XAxis dataKey="no" tick={tick} stroke={grid} interval={9} />
              <YAxis domain={['dataMin - 0.02', 'dataMax + 0.01']} tick={tick} stroke={grid} unit="V" width={54} />
              <RTooltip contentStyle={tooltipStyle} labelFormatter={(v) => t(lang, `单体 ${v}`, `Cell ${v}`)} />
              <ReferenceLine y={3.331} stroke="rgba(255,255,255,0.25)" strokeDasharray="4 4" />
              <Bar dataKey="voltage" name={t(lang, '电压', 'Voltage')} fill="#4dd8ff" />
              <Brush dataKey="no" height={22} stroke="#00d4ff" travellerWidth={8} fill="rgba(0,180,216,0.08)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  );

  // ---------- 电表 ----------
  const renderMeters = () => (
    <>
      <div className="dev-grid">
        {dev.meters.map((m) => (
          <div className="dev-card" key={m.id}>
            <div className="dev-card-head">
              <span className={`dot ${m.state === '正常' ? 'online' : 'warn'}`} />
              <span className="dev-name">{m.name}</span>
              <span className="dev-status">{t(lang, m.state, EXTRA_EN[m.state])}</span>
            </div>
            <div className="dev-tags"><Tag color="blue">{t(lang, m.state, EXTRA_EN[m.state])}</Tag><Tag>{m.wiring}</Tag><Tag>{m.id}</Tag></div>
            <div className="dev-kv">
              <Kpi label={t(lang, '三相电压', '3-Phase Voltage')} value={m.u.join(' / ')} unit="V" />
              <Kpi label={t(lang, '三相电流', '3-Phase Current')} value={m.i.join(' / ')} unit="A" />
              <Kpi label={t(lang, '有功功率', 'Active Power')} value={m.p} unit="kW" />
              <Kpi label={t(lang, '无功功率', 'Reactive Power')} value={m.q} unit="kVar" />
              <Kpi label={t(lang, '功率因数', 'Power Factor')} value={m.pf} unit="—" />
              <Kpi label={t(lang, '频率', 'Frequency')} value={m.freq} unit="Hz" />
              <Kpi label={t(lang, '电能示数', 'Energy Reading')} value={m.ep} unit="kWh" />
            </div>
          </div>
        ))}
      </div>
      <div className="ems-note">{t(lang, '一般电站包含一个总电表、站用电表与用户侧电表；本面板状态栏展示的是电表通信状态。', 'A typical station includes a main meter, a station-service meter and a user-side meter; the status bar here shows the meter communication state.')}</div>
    </>
  );

  // ---------- 空调 ----------
  const renderHvac = () => (
    <>
      <div className="dev-grid">
        {dev.hvac.map((h, i) => (
          <div className="dev-card" key={h.id}>
            <div className="dev-card-head">
              <span className={`dot ${h.state === '正常' ? 'online' : 'warn'}`} />
              <span className="dev-name">{h.name}</span>
              <span className="dev-status">{t(lang, h.state, EXTRA_EN[h.state])}</span>
            </div>
            <div className="dev-tags">
              <Tag color="blue">{h.mode}</Tag>
              <Tag>{h.running ? t(lang, '运行', 'Running') : t(lang, '停机', 'Stopped')}</Tag>
              {h.alarm ? <Tag color="orange">{t(lang, h.alarm, EXTRA_EN[h.alarm])}</Tag> : null}
              {h.state !== '正常' ? <Tag color="red">{t(lang, '最后上报', 'Last report')} {LAST_REPORT}</Tag> : null}
            </div>
            <div className="dev-kv">
              <Kpi label={t(lang, '设定温度', 'Set Temp')} value={h.setTemp} unit="℃" />
              <Kpi label={t(lang, '回风温度', 'Return Air Temp')} value={h.returnTemp} unit="℃" />
              <Kpi label={t(lang, '出风温度', 'Supply Air Temp')} value={h.supplyTemp} unit="℃" />
              <Kpi label={t(lang, '环境湿度', 'Ambient Humidity')} value={h.humidity} unit="%RH" />
              <Kpi label={t(lang, '风机', 'Fan')} value={h.fan} unit="—" />
              <Kpi label={t(lang, '压缩机', 'Compressor')} value={h.compressor} unit="—" />
            </div>
            <div style={{ marginTop: 10 }}>
              <Space>
                <span className="ems-inline-label">{t(lang, '设定温度', 'Set Temp')}</span>
                <Select
                  size="small"
                  value={hvacTemps[i]}
                  style={{ width: 90 }}
                  onChange={(v) => setHvacTemps((arr) => arr.map((x, j) => (j === i ? v : x)))}
                  options={[22, 23, 24, 25, 26, 27, 28].map((v) => ({ value: v, label: `${v} ℃` }))}
                />
                <Button size="small" onClick={() => message.success(t(lang, `${h.name} 参数保存成功`, `Parameters saved for ${h.name}`))}>{t(lang, '保存', 'Save')}</Button>
              </Space>
              <div className="ems-note">{t(lang, '参数框默认读取设备原始数值；修改保存后提示「保存成功」。', 'The parameter box reads the device raw values by default; "saved successfully" is prompted after modification and saving.')}</div>
            </div>
          </div>
        ))}
      </div>
      <ControlBar title={t(lang, '空调设备操作', 'HVAC Device Operation')} sub={t(lang, '开关机需二次口令', 'Power on/off requires password verification')} lockedText={t(lang, '需二次口令', 'Password Required')} unlocked={unlocked} gate={gate}>
        <Space wrap>
          {dev.hvac.map((h) => (
            <Button key={h.id} size="small" type={h.running ? 'default' : 'primary'} onClick={() => message.success(t(lang, `${h.name} 下发「${h.running ? '关机' : '开机'}」指令成功`, `"${h.running ? 'Power Off' : 'Power On'}" command sent to ${h.name} successfully`))}>
              {h.name} {h.running ? t(lang, '关机', 'Power Off') : t(lang, '开机', 'Power On')}
            </Button>
          ))}
        </Space>
        <div className="ems-note">{t(lang, '开关机按钮按当前运行状态显示「开机 / 关机」两种文案，下发前需二次口令校验。', 'Power on/off buttons show "Power On / Power Off" per current running state; password verification is required before issuing.')}</div>
      </ControlBar>
    </>
  );

  // ---------- 视频监控（本类型独有） ----------
  const renderVideo = () => (
    <>
      <div className="video-grid">
        {video.map((v) => (
          <div className={`video-tile${v.state !== '在线' ? ' offline' : ''}`} key={v.key}>
            <div className="video-screen">
              {v.state === '在线' ? (
                <div className="video-screen-grid">
                  <span className="video-screen-ico">◉</span>
                </div>
              ) : (
                <div className="video-signal-lost">
                  <span className="video-screen-ico big">◌</span>
                  {t(lang, '视频信号丢失', 'Video Signal Lost')}
                </div>
              )}
            </div>
            <div className="video-tile-foot">
              <span className={`dot ${v.state === '在线' ? 'online' : 'offline'}`} />
              <span className="video-tile-name">{v.name}</span>
              <span className="video-tile-meta">{v.resolution} · {v.ip}</span>
            </div>
            <div className="dev-tags" style={{ marginTop: 8 }}>
              <Tag color={v.state === '在线' ? 'green' : 'red'}>{t(lang, v.state, EXTRA_EN[v.state])}</Tag>
              <Tag>{v.state === '在线' ? t(lang, '无告警', 'No Alarm') : t(lang, '告警消息：视频信号丢失', 'Alarm: Video Signal Lost')}</Tag>
            </div>
          </div>
        ))}
      </div>
      <div className="ems-note">{t(lang, '视频监控为能源管家类型独有模块：展示各通道运行状态与告警消息（本类型不含光伏逆变器、液冷、DCDC、UPS、充电桩）。', 'Video monitoring is a module unique to the Energy Manager type: shows each channel running state and alarm messages (this type has no PV inverter, liquid cooling, DCDC, UPS or charging piles).')}</div>
    </>
  );

  // ---------- 消防设备（本类型独有 · 联动环） ----------
  const fireGroups = FIRE_LINK_RING.map((cat) => ({
    cat,
    items: dev.fire.filter((f) => f.cat === cat),
  }));

  const renderFire = () => (
    <>
      <div className="panel">
        <div className="panel-title"><span>{t(lang, '消防设备', 'Fire Devices')}</span><small>{t(lang, `按 ${FIRE_LINK_RING.join(' / ')} 顺序排列 · 一行两卡`, `Ordered by ${FIRE_LINK_RING.map((n) => t(lang, n)).join(' / ')} · two cards per row`)}</small></div>
        {fireGroups.map((g) => (
          <div key={g.cat} style={{ marginBottom: 16 }}>
            <div className="ems-inline-label" style={{ marginBottom: 8 }}>{t(lang, g.cat)}</div>
            <div className="dev-grid dev-grid-2">
              {g.items.map((f) => (
                <div className="dev-card" key={f.id}>
                  <div className="dev-card-head">
                    <span className={`dot ${f.state === '正常' ? 'online' : 'fault'}`} />
                    <span className="dev-name">{f.name}</span>
                    <span className="dev-status">{f.state === '正常' ? t(lang, '在线', 'Online') : t(lang, '告警', 'Alarm')}</span>
                  </div>
                  {f.cat === '温湿度计' ? (
                    <div className="dev-kv">
                      <Kpi label={t(lang, '温度', 'Temperature')} value={f.temp.toFixed(1)} unit="℃" />
                      <Kpi label={t(lang, '湿度', 'Humidity')} value={f.humidity.toFixed(1)} unit="%RH" />
                    </div>
                  ) : null}
                  <div className="dev-kv">
                    {f.rows.map(([k, v]) => (
                      <div className="dev-kv-row" key={k}>
                        <span className="dev-kv-k">{k}</span>
                        <span className="dev-kv-v">{v}</span>
                      </div>
                    ))}
                  </div>
                  {f.state !== '正常' ? <div className="dev-tags"><Tag color="red">{t(lang, '设备中断 · 最后上报', 'Device interrupted · Last report')} {LAST_REPORT}</Tag></div> : null}
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="ems-note">{t(lang, '温湿度计读数保留 0.1 ℃ / 0.1 %RH 精度；设备中断时展示中断状态与最后上报时间。', 'TH sensor readings keep 0.1 ℃ / 0.1 %RH precision; when a device is interrupted, the interrupted state and last report time are shown.')}</div>
      </div>

      <ControlBar title={t(lang, '消防设备操作', 'Fire Device Operation')} sub={t(lang, '联动环配置需二次口令', 'Linkage ring configuration requires password verification')} lockedText={t(lang, '需二次口令', 'Password Required')} unlocked={unlocked} gate={gate}>
        <Button type="primary" onClick={() => setFireModal(true)}>{t(lang, '配置消防设备联动环', 'Configure Fire Linkage Ring')}</Button>
        <div className="ems-note">{t(lang, '配置消防联动环后，触发条件满足时按环内顺序联动，形成整站消防联动。', 'After configuring the fire linkage ring, when trigger conditions are met, devices act in ring order to form whole-station fire linkage.')}</div>
        <div className="ems-ring" style={{ marginTop: 14 }}>
          {FIRE_LINK_RING.map((name, i) => (
            <React.Fragment key={name}>
              <span className={`ems-ring-item${ringBound ? ' on' : ''}`}>{t(lang, name)}</span>
              {i < FIRE_LINK_RING.length - 1 ? <span className="ems-ring-arrow">→</span> : null}
            </React.Fragment>
          ))}
        </div>
      </ControlBar>

      <Modal
        title={t(lang, '配置消防设备联动环', 'Configure Fire Linkage Ring')}
        open={fireModal}
        onCancel={() => setFireModal(false)}
        okText={t(lang, '保存联动环', 'Save Linkage Ring')}
        cancelText={t(lang, '取消', 'Cancel')}
        onOk={() => { setRingBound(true); setFireModal(false); message.success(t(lang, '消防设备联动环配置成功', 'Fire device linkage ring configured successfully')); }}
      >
        <div className="ems-note" style={{ marginTop: 0 }}>{t(lang, '按以下顺序串联各消防设备，形成消防联动：', 'Link the fire devices in the following order to form fire linkage:')}</div>
        <div className="ems-ring" style={{ marginTop: 12 }}>
          {FIRE_LINK_RING.map((name, i) => (
            <React.Fragment key={name}>
              <span className="ems-ring-item on">{t(lang, name)}</span>
              {i < FIRE_LINK_RING.length - 1 ? <span className="ems-ring-arrow">→</span> : null}
            </React.Fragment>
          ))}
        </div>
      </Modal>
    </>
  );

  const items = [
    { key: 'rt', label: t(lang, '实时运行', 'Realtime'), children: <RealtimeTab dev={dev} st={st} /> },
    { key: 'pcs', label: 'PCS', children: renderPcs() },
    { key: 'bms', label: t(lang, 'BMS 总览', 'BMS Overview'), children: renderBms() },
    { key: 'cluster', label: t(lang, 'BMS 电池簇', 'BMS Clusters'), children: renderCluster() },
    { key: 'cell', label: t(lang, '单体电池', 'Cells'), children: renderCells() },
    { key: 'meter', label: t(lang, '电表', 'Meters'), children: renderMeters() },
    { key: 'hvac', label: t(lang, '空调', 'HVAC'), children: renderHvac() },
    { key: 'video', label: t(lang, '视频监控', 'Video'), children: renderVideo() },
    { key: 'fire', label: t(lang, '消防设备', 'Fire Devices'), children: renderFire() },
  ];

  // 子页切换对齐光储充「控制中心」顶部胶囊 Tabs
  return <ScrollTabs items={items} />;
}

// 实时运行：站级宏观数据 + 站级有功/无功功率曲线 + 电池堆 SOC 曲线（文档 2.7.3 实时运行）
function RealtimeTab({ dev, st }) {
  const { lang } = useLang();
  const ov = getOverview(st.id);
  const curve = getPowerCurve(st.id);
  const data = curve.map((c) => ({ clock: c.clock, active: c.storage, reactive: +(Math.abs(c.storage) / 12).toFixed(1), soc: c.soc }));
  const last = data[data.length - 1] || {};
  return (
    <>
      <div className="kpi-grid kpi-grid-5">
        <div className="kpi-card"><div className="kpi-label">{t(lang, '站级有功功率', 'Station Active Power')}</div><div className="kpi-value">{ov.storagePower}<span className="unit"> kW</span></div></div>
        <div className="kpi-card"><div className="kpi-label">{t(lang, '站级无功功率', 'Station Reactive Power')}</div><div className="kpi-value">{dev.pcs[0].q}<span className="unit"> kVar</span></div></div>
        <div className="kpi-card"><div className="kpi-label">{t(lang, '今日充电量', 'Charge Today')}</div><div className="kpi-value">{ov.chargeToday}<span className="unit"> kWh</span></div></div>
        <div className="kpi-card"><div className="kpi-label">{t(lang, '今日放电量', 'Discharge Today')}</div><div className="kpi-value">{ov.dischargeToday}<span className="unit"> kWh</span></div></div>
        <div className="kpi-card"><div className="kpi-label">{t(lang, '当前 SOC', 'Current SOC')}</div><div className="kpi-value">{ov.soc}<span className="unit"> %</span></div></div>
      </div>

      <div className="panel">
        <div className="panel-title"><span>{t(lang, '站级功率曲线', 'Station Power Curves')}</span><small>{t(lang, '今日 0 点 ~ 次日 6 点 · 有功 / 无功（量纲不同，左右双轴）', 'Today 00:00 ~ next day 06:00 · active / reactive (different units, dual axes)')}</small></div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data} margin={{ top: 10, right: 20, bottom: 6, left: 0 }}>
            <CartesianGrid stroke={grid} vertical={false} />
            <XAxis dataKey="clock" tick={tick} interval={11} minTickGap={24} stroke={grid} />
            <YAxis yAxisId="p" tick={tick} stroke={grid} unit="kW" width={56} />
            <YAxis yAxisId="q" orientation="right" tick={tick} stroke={grid} unit="kVar" width={56} />
            <RTooltip contentStyle={tooltipStyle} />
            <ReferenceLine yAxisId="p" y={0} stroke="rgba(255,255,255,0.25)" />
            <Line yAxisId="p" type="stepAfter" dataKey="active" name={t(lang, '有功功率', 'Active Power')} stroke="#52e8b0" strokeWidth={2} dot={false} />
            <Line yAxisId="q" type="stepAfter" dataKey="reactive" name={t(lang, '无功功率', 'Reactive Power')} stroke="#4dd8ff" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="panel">
        <div className="panel-title"><span>{t(lang, '电池堆 SOC 曲线', 'Battery Pack SOC Curves')}</span><small>{t(lang, 'SOC 量纲（%），与功率分轴', 'SOC unit (%), separate axis from power')}</small></div>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data} margin={{ top: 10, right: 20, bottom: 6, left: 0 }}>
            <CartesianGrid stroke={grid} vertical={false} />
            <XAxis dataKey="clock" tick={tick} interval={11} minTickGap={24} stroke={grid} />
            <YAxis domain={[0, 100]} tick={tick} stroke={grid} unit="%" width={44} />
            <RTooltip contentStyle={tooltipStyle} />
            <Line type="linear" dataKey="soc" name={t(lang, '电池堆 SOC', 'Pack SOC')} stroke="#ffd166" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
        <div className="ems-note">{t(lang, `当前 SOC ${last.soc}%。`, `Current SOC ${last.soc}%.`)}</div>
      </div>
    </>
  );
}
